import { useClickOutside, useDebouncedEffect } from '@react-hookz/web';
import { useQuery } from '@tanstack/react-query';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import { company } from '~/company/Company';
import Modal from '~/components/Modal/Modal';
import AddressAutocomplete from '~/components/ModalDeliveryAddress/AddressAutocomplete';
import geocoderRequest from '~/components/ModalDeliveryAddress/geocoderRequest';
import useModalEditEffect from '~/components/ModalDeliveryAddress/useModalEditEffect';
import { useGeolocation } from '~/hooks/useGeolocation';
import { useGlobal } from '~/hooks/useGlobal';
import { useModal } from '~/hooks/useModal';
import { useSetMapCenter } from '~/hooks/useSetMapCenter';

import { mainStore, orderStore, userStore } from '~/stores';

import Icon from '../Icon/Icon';

import AddressAutocompleteDropDown from './AddressAutocompleteDropDown';
import { REDIRECT_QUERY_NAME } from './constants';
import { deliveryAddressStore } from './DeliveryAddressStore';
import Geocoding from './Geocoding/Geocoding';
import Geolocation from './Geolocation/Geolocation';
import GMap from './GMap/GMap';
import styles from './ModalDeliveryAddress.module.scss';
import { SaveButton } from './SaveButton/SaveButton';
import {
  saveAuthorizedAddress,
  saveUnauthorizedAddress,
  sendAddressChangedAnalytics,
  sendPostSaveAnalytics,
  updateEtaAndDeliveryMethod,
} from './saveAddressUtils';

interface AutocompleteResponse {
  data: {
    result: {
      address: string;
      postcode: null | string;
      city?: string;
      country?: string;
      region?: string;
      location: {
        latitude: number;
        longitude: number;
      };
    } | null;
  };
}

const ModalDeliveryAddress = () => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();
  const { requestLocation } = useGeolocation();
  const [isAutoCompleteOpen, setIsAutoCompleteOpen] = useState(false);
  const [addressInputText, setAddressInputText] = useState('');
  const [addressToSearch, setAddressToSearch] = useState('');
  const [autocompletePlaceId, setAutocompletePlaceId] = useState<string>();
  const [isPudoPopupOpen, setIsPudoPopupOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const editableAddressId = searchParams.get('editAddress');
  const redirectNext = searchParams.get(REDIRECT_QUERY_NAME);
  const addressFormRef = useRef<HTMLDivElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const { closeModal } = useModal();
  useSetMapCenter(userStore.deliveryAddress);

  const setAutocompleteActive = () => {
    setIsAutoCompleteOpen(true);
  };

  const handleCloseAddressModal = () => {
    closeModal([REDIRECT_QUERY_NAME]);
    deliveryAddressStore.setSelectedZone(null);
  };

  const handlePudoSelect = async (center: { lat: number; lng: number }) => {
    deliveryAddressStore.setIsLoading(true);
    try {
      const selectedZone = deliveryAddressStore.getSelectedPolygon(center);
      deliveryAddressStore.setSelectedZone(selectedZone);

      await deliveryAddressStore.geocoding(false, center);

      const deliveryAddress = deliveryAddressStore.deliveryAddress;
      if (!deliveryAddress?.city || !deliveryAddressStore.etaCalculation) {
        return;
      }

      const editAddressData = deliveryAddressStore.editAddressData;

      sendAddressChangedAnalytics(
        deliveryAddress,
        deliveryAddressStore.etaCalculation,
      );

      if (userStore.isAuthorized) {
        await saveAuthorizedAddress(deliveryAddress, editAddressData, {
          alwaysSetDeliveryAddress: true,
        });
      } else {
        saveUnauthorizedAddress(deliveryAddress, editAddressData);
      }

      await updateEtaAndDeliveryMethod(false);
      sendPostSaveAnalytics(deliveryAddress);
      handleCloseAddressModal();
    } finally {
      deliveryAddressStore.setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAutoCompleteOpen) {
      addressInputRef.current?.focus();
    }
  }, [isAutoCompleteOpen]);

  useEffect(() => {
    userStore.resetAddressSearchSessionToken();
    if (editableAddressId) {
      return;
    }
    requestLocation();
  }, []);

  useEffect(() => {
    setAddressInputText(deliveryAddressStore.inputAddressValue);
  }, [deliveryAddressStore.inputAddressValue]);

  useDebouncedEffect(
    () => setAddressToSearch(addressInputText),
    [addressInputText],
    500,
  );

  useClickOutside(addressFormRef, () => {
    setIsAutoCompleteOpen(false);
    setAddressInputText(deliveryAddressStore.inputAddressValue);
  });

  useEffect(() => {
    return () => {
      deliveryAddressStore.resetStore();
    };
  }, []);

  useEffect(() => {
    if (userStore.isAuthorized) {
      userStore.fetchAddresses();
    }
  }, [userStore.isAuthorized]);

  const { search } = useLocation();
  const params = new URLSearchParams(search);

  useModalEditEffect(params);

  useEffect(() => {
    setAutocompletePlaceId(undefined);
  }, [deliveryAddressStore.deliveryAddress?.placeId]);

  const {
    data: { result: autocompleteResult },
    isFetching,
  } = useQuery({
    enabled: !!userStore.addressSearchSessionToken && !!autocompletePlaceId,
    queryKey: ['autocomplete-get-place', autocompletePlaceId],
    initialData: {
      result: null,
    },
    queryFn: () =>
      geocoderRequest
        .post<AutocompleteResponse>('/autocomplete', {
          input: '',
          placeId: autocompletePlaceId,
          sessionToken: userStore.addressSearchSessionToken,
          countryCode: company.config.countryCode || '',
        })
        .then(({ data: { data } }) => data),
  });

  useEffect(() => {
    if (!autocompleteResult) {
      return;
    }

    const point = {
      lat: autocompleteResult.location.latitude,
      lng: autocompleteResult.location.longitude,
    };

    deliveryAddressStore.selectZoneByCoordinates(point);

    const useAutocompleteAddress = company.config.useAutocompleteAddress;

    deliveryAddressStore.setDeliveryAddress({
      zip: useAutocompleteAddress ? autocompleteResult.postcode || '' : '',
      country: useAutocompleteAddress ? autocompleteResult.country || '' : '',
      city: useAutocompleteAddress ? autocompleteResult.city || '' : '',
      region: useAutocompleteAddress ? autocompleteResult.region || '' : '',
      address1: useAutocompleteAddress ? autocompleteResult.address : '',
      address2: '',
      addressFloor: '',
      addressBld: '',
      addressApt: '',
      shortAddress: useAutocompleteAddress ? autocompleteResult.address : '',
      coordinates: point,
      placeId: mainStore.getPseudoId(),
      addressId: null,
      comment: '',
      instructions: [],
      type: 'home',
    });

    if (useAutocompleteAddress) {
      deliveryAddressStore.setInputAddressValue(autocompleteResult.address);
    }

    if (deliveryAddressStore.selectedZone) {
      deliveryAddressStore.geocoding(!!useAutocompleteAddress, {
        lng: autocompleteResult.location.longitude,
        lat: autocompleteResult.location.latitude,
      });
    } else {
      orderStore.requestETA();
    }
  }, [autocompleteResult, deliveryAddressStore.renderedZonesOnMap]);

  useEffect(() => {
    deliveryAddressStore.setIsLoading(isFetching);
  }, [isFetching]);

  useEffect(() => {
    if (editableAddressId) {
      const editableAddress = userStore.addressList.find(
        (item) => item.id === editableAddressId,
      );
      if (editableAddress) {
        const point = {
          lat: editableAddress.latitude,
          lng: editableAddress.longitude,
        };
        deliveryAddressStore.selectZoneByCoordinates(point);
      }
    }
  }, []);

  const autocompleteInput = (
    <div
      ref={addressFormRef}
      className={classes([
        'address__form-input-container',
        isAutoCompleteOpen && styles.addressInputContainerFocused,
        isAutoCompleteOpen && 'mobile__opened',
        company.addressModalDefault &&
          addressInputText.length > 0 &&
          styles.filled,
      ])}
    >
      <Icon type="location" size={24} className={styles.statusIcon} />
      <input
        data-input="address"
        placeholder={t(isAutoCompleteOpen ? 'typeLocation' : 'enterAddress')}
        value={addressInputText}
        onChange={(e) => setAddressInputText(e.target.value)}
        onFocus={() => setAutocompleteActive()}
        disabled={deliveryAddressStore.isLoading && !isAutoCompleteOpen}
        ref={addressInputRef}
      />
      {isAutoCompleteOpen && (
        <>
          <button
            data-button="address-close-autocomplete"
            className={classes([
              'button _no-padding _no-color',
              styles.backButton,
            ])}
            onClick={() => setIsAutoCompleteOpen(false)}
          >
            <Icon
              type="arrow"
              className={classes(['icon__rotate-180', styles.backButtonIcon])}
              size={24}
            />
          </button>
          <button
            data-button="address-reset-autocomplete"
            className={classes([
              'button _no-padding _no-color',
              styles.resetButton,
            ])}
            onClick={() => {
              setAddressInputText('');
              deliveryAddressStore.setDeliveryAddress(null);
              deliveryAddressStore.setInputAddressValue('');
            }}
          >
            <Icon
              type="plus"
              className={classes(['icon__rotate-45', styles.resetButtonIcon])}
              size={36}
            />
          </button>
        </>
      )}
      <AddressAutocompleteDropDown
        isOpen={isAutoCompleteOpen}
        className={styles.dropDown}
      >
        <AddressAutocomplete
          searchSessionToken={userStore.addressSearchSessionToken}
          searchText={addressToSearch}
          onSelect={(placeId, address) => {
            setIsAutoCompleteOpen(false);
            if (placeId) {
              setAutocompletePlaceId(placeId);
              setAddressInputText(address);
              userStore.resetAddressSearchSessionToken();
            }
          }}
          onLoadingStateChange={(isLoading) =>
            deliveryAddressStore.setIsLoading(isLoading)
          }
        />
      </AddressAutocompleteDropDown>
    </div>
  );

  return (
    <Modal
      isShow={true}
      className="_mobile-fullscreen modal-delivery-address__body"
      onBackdropDismiss={handleCloseAddressModal}
    >
      <div className="modal-delivery-address" data-company={company.name}>
        {isMobile && (
          <style>
            {`.alert {
              bottom: unset !important; top: calc(var(--offset-top) + 60px)
            }`}
          </style>
        )}
        {isAutoCompleteOpen && isMobile ? (
          <div
            className={classes([
              'modal-delivery-address__data',
              styles.addressContainer,
              isAutoCompleteOpen && isMobile && styles.addressContainerFocused,
            ])}
          >
            <div className="modal-delivery-address__form">
              {autocompleteInput}
            </div>
          </div>
        ) : (
          <div
            className={classes([
              'modal-delivery-address__data',
              styles.addressContainer,
              isAutoCompleteOpen && isMobile && styles.addressContainerFocused,
            ])}
          >
            <p className="h2 modal-delivery-address__title">
              {t(
                redirectNext
                  ? 'enterDeliveryToContinue'
                  : 'enterDeliveryAddress',
              )}
            </p>
            <div className="modal-delivery-address__form">
              {autocompleteInput}{' '}
              {/* {company.match({
              citydrinks: (
                <div className="address__form-input-container">
                  <Icon type="home-edit" size={24} />
                  <input
                    placeholder={`${t('address2')}*`}
                    value={deliveryAddressStore.editAddressData.address2}
                    onChange={(e) =>
                      deliveryAddressStore.setDeliveryAddress2(e.target.value)
                    }
                  />
                </div>
              ),
              default: null,
            })} */}
              <div className={styles.inputGroup}>
                <div className="address__form-input-container">
                  <input
                    data-input="address-building"
                    placeholder={`${t('Bld')}${company.isBuildingOptional ? '' : '*'}`}
                    value={deliveryAddressStore.editAddressData.addressBld}
                    onChange={(e) =>
                      deliveryAddressStore.setDeliveryAddressBld(e.target.value)
                    }
                  />
                </div>
                <div className="address__form-input-container">
                  <input
                    data-input="address-floor"
                    placeholder={`${t('Floor')}`}
                    value={deliveryAddressStore.editAddressData.addressFloor}
                    onChange={(e) =>
                      deliveryAddressStore.setDeliveryAddressFloor(
                        e.target.value,
                      )
                    }
                  />
                </div>
                <div className="address__form-input-container">
                  <input
                    data-input="address-apt"
                    placeholder={`${t('Apt')}`}
                    value={deliveryAddressStore.editAddressData.addressApt}
                    onChange={(e) =>
                      deliveryAddressStore.setDeliveryAddressApt(e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="address__form-selector">
                <button
                  data-button="address-home"
                  className={classes([
                    'button address__form-selector-btn',
                    deliveryAddressStore.editAddressData.type === 'home' &&
                      'selected',
                  ])}
                  onClick={() => deliveryAddressStore.setAddressType('home')}
                >
                  {t('addressHome')}
                </button>
                <button
                  data-button="address-custom"
                  className={classes([
                    'button address__form-selector-btn',
                    deliveryAddressStore.editAddressData.type !== 'home' &&
                      'selected',
                  ])}
                  onClick={() => deliveryAddressStore.setAddressType('custom')}
                >
                  {t('addressCustom')}
                </button>
              </div>
              {deliveryAddressStore.editAddressData.type !== 'home' && (
                <div className="address__form-input-container">
                  <input
                    data-input="address-custom-name"
                    placeholder={t('addressCustomName')}
                    value={deliveryAddressStore.editAddressData.type}
                    onChange={(e) =>
                      deliveryAddressStore.setAddressTypeValue(e.target.value)
                    }
                  />
                </div>
              )}
            </div>
            <div className="address__form-button-container">
              <SaveButton redirectNext={redirectNext} />
            </div>
          </div>
        )}

        <div
          className={classes([
            'modal-delivery-address__map',
            isAutoCompleteOpen && 'hide',
          ])}
        >
          <Geocoding />
          <GMap
            onPudoSelect={handlePudoSelect}
            onPudoPopupChange={setIsPudoPopupOpen}
          />
          {!isPudoPopupOpen && !company.isPudoOnly && (
            <Icon
              type="pin"
              size={company.match({
                citydrinks: 44,
                v3: 44,
                default: 64,
              })}
              className="modal-delivery-address__map-pin"
            />
          )}
          <Geolocation />
          <button
            data-button="address-close"
            className="button _no-padding _no-color input-form__close"
            onClick={handleCloseAddressModal}
          >
            <Icon
              type="plus"
              className="icon__rotate-45"
              size={company.match({
                citydrinks: 36,
                v3: 36,
                default: isMobile ? 16 : 24,
              })}
            />
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default observer(ModalDeliveryAddress);
